import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FaChartLine, FaDollarSign, FaUsers, FaHeart, FaComment, FaShare,
  FaEye, FaArrowUp, FaArrowDown, FaLightbulb, FaClock, FaGlobe,
  FaVideo, FaImage, FaFileAlt, FaWallet, FaHistory
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';

const CreatorsHub = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [analytics, setAnalytics] = useState(null);
  const [insights, setInsights] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [audience, setAudience] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCreatorData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCreatorData = async () => {
    try {
      const [analyticsRes, insightsRes, earningsRes, audienceRes, performanceRes] = await Promise.all([
        api.get('/creators/analytics'),
        api.get('/creators/insights'),
        api.get('/creators/earnings'),
        api.get('/creators/audience'),
        api.get('/creators/performance')
      ]);
      
      setAnalytics(analyticsRes.data);
      setInsights(insightsRes.data);
      setEarnings(earningsRes.data);
      setAudience(audienceRes.data);
      setPerformance(performanceRes.data);
    } catch (error) {
      console.error('Failed to load creator data:', error);
      if (error.response?.status === 401) {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, change, color = 'blue' }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-shadow"
    >
      <div className="flex items-start justify-between">
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`text-xl text-${color}-500`} />
        </div>
        {change !== undefined && (
          <span className={`flex items-center text-sm font-medium ${change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
            {change >= 0 ? <FaArrowUp className="mr-1" /> : <FaArrowDown className="mr-1" />}
            {Math.abs(change)}%
          </span>
        )}
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mt-4">{value}</h3>
      <p className={`text-gray-500 text-sm mt-1 ${label.includes('Point') ? 'font-arthaus' : ''}`}>{label}</p>
    </motion.div>
  );

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FaChartLine },
    { id: 'earnings', label: 'Earnings', icon: FaDollarSign },
    { id: 'audience', label: 'Audience', icon: FaUsers },
    { id: 'content', label: 'Content', icon: FaFileAlt },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="grid grid-cols-1 md:grid-cols-12">
          <Sidebar />
          <main className="col-span-12 md:col-span-10 p-6 md:p-10">
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="grid grid-cols-1 md:grid-cols-12">
        <Sidebar />
        
        <main className="col-span-12 md:col-span-10 p-6 md:p-10">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Creators Hub</h1>
            <p className="text-gray-500 mt-2">Track your performance, earnings, and audience insights</p>
          </div>

          {/* Tabs */}
          <div className="flex space-x-2 mb-8 overflow-x-auto">
            {tabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all whitespace-nowrap ${
                  activeTab === id
                    ? 'bg-gradient-to-r from-[#4A90E2] to-[#A855F7] text-white shadow-lg shadow-blue-500/30'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <Icon />
                <span>{label}</span>
              </button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && analytics && (
            <div className="space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                  icon={FaFileAlt}
                  label="Total Points"
                  value={analytics.overview.total_posts}
                  color="blue"
                />
                <StatCard
                  icon={FaHeart}
                  label="Total Likes"
                  value={analytics.overview.total_likes.toLocaleString()}
                  change={insights?.weekly_stats?.growth_percentage}
                  color="red"
                />
                <StatCard
                  icon={FaComment}
                  label="Total Comments"
                  value={analytics.overview.total_comments.toLocaleString()}
                  color="green"
                />
                <StatCard
                  icon={FaUsers}
                  label="Followers"
                  value={analytics.overview.followers.toLocaleString()}
                  change={audience?.growth?.growth_rate}
                  color="purple"
                />
              </div>

              {/* Engagement Rate & Insights */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Engagement Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl p-6 border border-gray-200"
                >
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Engagement Rate</h3>
                  <div className="flex items-center justify-center py-8">
                    <div className="relative">
                      <svg className="w-40 h-40 transform -rotate-90">
                        <circle
                          cx="80"
                          cy="80"
                          r="70"
                          stroke="#E5E7EB"
                          strokeWidth="12"
                          fill="none"
                        />
                        <circle
                          cx="80"
                          cy="80"
                          r="70"
                          stroke="url(#gradient)"
                          strokeWidth="12"
                          fill="none"
                          strokeLinecap="round"
                          strokeDasharray={`${analytics.overview.engagement_rate * 44} 440`}
                        />
                        <defs>
                          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#4A90E2" />
                            <stop offset="100%" stopColor="#A855F7" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <span className="text-4xl font-bold text-gray-900">{analytics.overview.engagement_rate}</span>
                          <span className="text-gray-500 block text-sm">per post</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Content Tips */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl p-6 border border-gray-200"
                >
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <FaLightbulb className="text-yellow-500 mr-2" />
                    Content Tips
                  </h3>
                  <div className="space-y-4">
                      {insights?.content_suggestions?.map((tip, index) => (
                      <div key={`tip-${index}`} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-[#4A90E2] to-[#A855F7] flex items-center justify-center text-white text-xs font-bold">
                          {index + 1}
                        </div>
                        <p className="text-gray-700 text-sm">{tip}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-blue-700 text-sm flex items-center">
                      <FaClock className="mr-2" />
                      Best time to post: <strong className="ml-1">{insights?.best_time_to_post}</strong>
                    </p>
                  </div>
                </motion.div>
              </div>

              {/* Top Posts */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl p-6 border border-gray-200"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-4">Top Performing Posts</h3>
                <div className="space-y-4">
                  {analytics.top_posts.slice(0, 5).map((post, index) => (
                    <div key={post.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center space-x-4">
                        <span className="text-2xl font-bold text-gray-300">#{index + 1}</span>
                        <p className="text-gray-700 line-clamp-1">{post.content || 'Media post'}</p>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span className="flex items-center"><FaHeart className="text-red-400 mr-1" /> {post.likes_count}</span>
                        <span className="flex items-center"><FaComment className="text-blue-400 mr-1" /> {post.comments_count}</span>
                        <span className="flex items-center"><FaShare className="text-green-400 mr-1" /> {post.shares_count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          )}

          {/* Earnings Tab */}
          {activeTab === 'earnings' && earnings && (
            <div className="space-y-6">
              {/* Earnings Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                  icon={FaWallet}
                  label="Total Balance"
                  value={`$${earnings.summary.total_balance.toLocaleString()}`}
                  color="green"
                />
                <StatCard
                  icon={FaClock}
                  label="Pending Payout"
                  value={`$${earnings.summary.pending_payout.toLocaleString()}`}
                  color="yellow"
                />
                <StatCard
                  icon={FaHistory}
                  label="Last Payout"
                  value={`$${earnings.summary.last_payout.toLocaleString()}`}
                  color="blue"
                />
                <StatCard
                  icon={FaDollarSign}
                  label="Content Earnings"
                  value={`$${earnings.breakdown.content_earnings.toLocaleString()}`}
                  color="purple"
                />
              </div>

              {/* Earnings Breakdown */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl p-6 border border-gray-200"
                >
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Revenue Breakdown</h3>
                  <div className="space-y-4">
                    {[
                      { label: 'Content Earnings', value: earnings.breakdown.content_earnings, color: 'purple' },
                      { label: 'Tips Received', value: earnings.breakdown.tips, color: 'pink' },
                      { label: 'Subscriptions', value: earnings.breakdown.subscriptions, color: 'blue' },
                      { label: 'Ad Revenue', value: earnings.breakdown.ad_revenue, color: 'green' }
                    ].map((item) => (
                      <div key={item.label} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`w-3 h-3 rounded-full bg-${item.color}-500`}></div>
                          <span className="text-gray-600">{item.label}</span>
                        </div>
                        <span className="font-bold text-gray-900">${item.value.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Monthly Earnings Chart */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl p-6 border border-gray-200"
                >
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Monthly Earnings</h3>
                  <div className="flex items-end justify-between h-48 space-x-2">
                    {earnings.monthly_history.map((month, index) => {
                      const maxEarnings = Math.max(...earnings.monthly_history.map(m => m.earnings));
                      const height = (month.earnings / maxEarnings) * 100;
                      return (
                        <div key={month.month || `month-${index}`} className="flex-1 flex flex-col items-center">
                          <div 
                            className="w-full bg-gradient-to-t from-[#4A90E2] to-[#A855F7] rounded-t-lg transition-all hover:from-[#3A80D2] hover:to-[#9845E7]"
                            style={{ height: `${height}%`, minHeight: '20px' }}
                            title={`$${month.earnings}`}
                          ></div>
                          <span className="text-xs text-gray-500 mt-2">{month.month}</span>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              </div>

              {/* Payout Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl p-6 text-white"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold">Ready to withdraw?</h3>
                    <p className="text-green-100 mt-1">Minimum payout: $50.00</p>
                  </div>
                  <button className="bg-white text-green-600 px-6 py-3 rounded-lg font-bold hover:bg-green-50 transition-colors">
                    Request Payout
                  </button>
                </div>
              </motion.div>
            </div>
          )}

          {/* Audience Tab */}
          {activeTab === 'audience' && audience && (
            <div className="space-y-6">
              {/* Audience Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                  icon={FaUsers}
                  label="Total Followers"
                  value={audience.total_followers.toLocaleString()}
                  color="purple"
                />
                <StatCard
                  icon={FaArrowUp}
                  label="New This Week"
                  value={`+${audience.growth.new_followers_week}`}
                  change={audience.growth.growth_rate}
                  color="green"
                />
                <StatCard
                  icon={FaEye}
                  label="Profile Visits"
                  value={performance?.reach?.profile_visits?.toLocaleString() || '0'}
                  color="blue"
                />
                <StatCard
                  icon={FaGlobe}
                  label="Reach"
                  value={performance?.reach?.unique_viewers?.toLocaleString() || '0'}
                  color="indigo"
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Age Demographics */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl p-6 border border-gray-200"
                >
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Age Distribution</h3>
                  <div className="space-y-4">
                    {audience.demographics.age_groups.map((group) => (
                      <div key={group.range}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600">{group.range}</span>
                          <span className="font-medium text-gray-900">{group.percentage}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-[#4A90E2] to-[#A855F7] h-2 rounded-full transition-all"
                            style={{ width: `${group.percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Top Locations */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl p-6 border border-gray-200"
                >
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Top Locations</h3>
                  <div className="space-y-4">
                    {audience.demographics.top_locations.map((location, index) => (
                      <div key={location.country} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <span className="text-lg font-bold text-gray-300">#{index + 1}</span>
                          <span className="text-gray-700">{location.country}</span>
                        </div>
                        <span className="font-bold text-gray-900">{location.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>

              {/* Activity Insights */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl p-6 border border-gray-200"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-4">Audience Activity</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <p className="text-purple-700 text-sm font-medium">Most Active Hours</p>
                    <p className="text-purple-900 font-bold mt-1">{audience.activity.most_active_hours.join(', ')}</p>
                  </div>
                  <div className="p-4 bg-pink-50 rounded-lg">
                    <p className="text-pink-700 text-sm font-medium">Most Active Days</p>
                    <p className="text-pink-900 font-bold mt-1">{audience.activity.most_active_days.join(', ')}</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-blue-700 text-sm font-medium">Avg Session Duration</p>
                    <p className="text-blue-900 font-bold mt-1">{audience.activity.avg_session_duration}</p>
                  </div>
                </div>
              </motion.div>
            </div>
          )}

          {/* Content Tab */}
          {activeTab === 'content' && performance && (
            <div className="space-y-6">
              {/* Content Type Performance */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { type: 'text', icon: FaFileAlt, label: 'Text Points', color: 'blue', arthaus: true },
                  { type: 'image', icon: FaImage, label: 'Image Points', color: 'green', arthaus: true },
                  { type: 'video', icon: FaVideo, label: 'Video Points', color: 'purple', arthaus: true }
                ].map(({ type, icon: Icon, label, color }) => (
                  <motion.div
                    key={type}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-xl p-6 border border-gray-200"
                  >
                    <div className={`w-12 h-12 rounded-lg bg-${color}-100 flex items-center justify-center mb-4`}>
                      <Icon className={`text-xl text-${color}-500`} />
                    </div>
                    <h4 className={`text-lg font-bold text-gray-900 ${label.includes('Point') ? 'font-arthaus' : ''}`}>{label}</h4>
                    <div className="mt-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Total Posts</span>
                        <span className="font-bold">{performance.content_performance[type]?.posts || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Avg. Engagement</span>
                        <span className="font-bold">{performance.content_performance[type]?.avg_engagement || 0}</span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Engagement Chart */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl p-6 border border-gray-200"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-4">30-Day Engagement Trend</h3>
                <div className="h-64 flex items-end space-x-1">
                  {performance.daily_engagement.map((day, index) => {
                    const total = day.likes + day.comments + day.shares;
                    const maxEngagement = Math.max(...performance.daily_engagement.map(d => d.likes + d.comments + d.shares));
                    const height = (total / maxEngagement) * 100;
                    return (
                      <div 
                        key={day.date || `day-${index}`} 
                        className="flex-1 bg-gradient-to-t from-[#4A90E2] to-[#A855F7] rounded-t hover:from-[#3A80D2] hover:to-[#9845E7] transition-all cursor-pointer"
                        style={{ height: `${height}%`, minHeight: '4px' }}
                        title={`${day.date}: ${total} engagements`}
                      ></div>
                    );
                  })}
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-2">
                  <span>30 days ago</span>
                  <span>Today</span>
                </div>
              </motion.div>

              {/* Reach Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-r from-[#4A90E2] to-[#A855F7] rounded-xl p-6 text-white"
              >
                <h3 className="text-xl font-bold mb-4">Reach Overview</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <p className="text-blue-100 text-sm">Total Impressions</p>
                    <p className="text-2xl font-bold">{performance.reach.total_impressions.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-blue-100 text-sm">Unique Viewers</p>
                    <p className="text-2xl font-bold">{performance.reach.unique_viewers.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-blue-100 text-sm">Profile Visits</p>
                    <p className="text-2xl font-bold">{performance.reach.profile_visits.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-blue-100 text-sm">Link Clicks</p>
                    <p className="text-2xl font-bold">{performance.reach.link_clicks.toLocaleString()}</p>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default CreatorsHub;
